import { TellescopeModelFromName, useAuth, usePermissionQuery } from "@tellescope/tanstack-query";
import {
  Badge,
  Button,
  cn,
  DEFAULT_HOSTS,
  getSelectedHost,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@tellescope/ui";
import { Copy, Ellipsis, Pencil, Trash2, Link as LinkIcon, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FALLBACK_EVENT_COLOR_HEX } from "../../utils";
import { useMemo, useState } from "react";
import { buildBookingPageURL, isDevelopment, isStaging } from "@tellescope/utils";

export interface BookingPageCardProps {
  bookingPage: TellescopeModelFromName<"appointment_booking_pages">;
  onClick?: (() => void) | { to: string; replace?: boolean };
  onDelete?: () => void;
  onDuplicate?: (() => void) | { to: string; replace?: boolean };
  onCopyLink?: () => void;
  onCreateNote?: (() => void) | { to: string; replace?: boolean };
}

export const BookingPageCard = ({
  bookingPage,
  onClick,
  onDelete,
  onDuplicate,
  onCopyLink,
  onCreateNote,
}: BookingPageCardProps) => {
  const { t } = useTranslation("calendar");
  const { selectedOrganization } = useAuth();
  const [popoverOpen, setPopoverOpen] = useState(false);

  const {
    delete: { access: deleteAccess },
    create: { access: duplicateAccess },
    update: { access: updateAccess },
  } = usePermissionQuery({
    model: "appointment_booking_pages",
    actions: ["delete", "create", "update"],
    record: bookingPage,
  });
  const {
    update: { access: createNoteAccess },
  } = usePermissionQuery({
    model: "endusers",
    actions: ["update"],
  });
  const [stagingHost, productionHost] = DEFAULT_HOSTS;
  const apiHost = isStaging() ? stagingHost.url : isDevelopment() ? getSelectedHost().url : productionHost.url;
  const bookingLink = buildBookingPageURL({
    subdomain: selectedOrganization?.subdomain,
    businessId: bookingPage.businessId,
    pageId: bookingPage.id,
    apiHost,
  });
  const canDelete = deleteAccess && !!onDelete;
  const canDuplicate = duplicateAccess && !!onDuplicate;
  const canUpdate = updateAccess && !!onClick;
  const canCreateNote = createNoteAccess && !!onCreateNote;
  const Component = canUpdate
    ? typeof onClick === "function"
      ? "button"
      : typeof onClick === "object"
      ? Link
      : "div"
    : "div";

  const reasonLabelMap = useMemo(
    () => ({
      Required: t("Required"),
      Optional: t("Optional"),
      "Do Not Collect": t("Do Not Collect"),
    }),
    [t]
  );

  return (
    <div
      data-testid={`booking-page-card-${bookingPage.id}`}
      className={cn(
        "border rounded-md flex flex-col group relative",
        Component !== "div" && "transition-all hover:shadow-sm"
      )}
    >
      <Component
        className="flex flex-col grow"
        to={typeof onClick === "object" ? onClick.to : ""}
        replace={typeof onClick === "object" ? onClick.replace : undefined}
        onClick={typeof onClick === "function" ? onClick : undefined}
      >
        <div className="p-3 space-y-2 grow">
          <span className="flex items-center gap-2 flex-wrap">
            <div
              className={cn(
                "rounded-full h-2.5 w-2.5 text-nowrap",
                !bookingPage.primaryColor && "border border-gray-300"
              )}
              style={{ backgroundColor: bookingPage.primaryColor || FALLBACK_EVENT_COLOR_HEX }}
            />
            <p className="text-sm">{bookingPage.title}</p>
            <span className="flex items-center gap-1">
              {bookingPage.hiddenFromPortal && (
                <Badge variant="outline" className="rounded-full">
                  {t("Hidden")}
                </Badge>
              )}
              {!!bookingPage.archivedAt && (
                <Badge variant="secondary" className="rounded-full">
                  {t("Archived")}
                </Badge>
              )}
            </span>
          </span>
          <div className="text-muted-foreground space-y-2">
            {!!bookingPage.portalDescription && (
              <span className="flex items-start gap-1 text-xs truncate line-clamp-2">
                {bookingPage.portalDescription}
              </span>
            )}
            <span className="flex items-start gap-1 text-xs truncate line-clamp-2">
              {t("Templates: {{amount}}", { amount: bookingPage.calendarEventTemplateIds.length })}
            </span>
            <span className="flex items-start gap-1 text-xs truncate line-clamp-2">
              {t("Locations: {{amount}}", { amount: bookingPage.locationIds.length })}
            </span>
            <span className="flex items-start gap-1 text-xs truncate line-clamp-2">
              {t("Lead time: {{amount}}h", { amount: bookingPage.hoursBeforeBookingAllowed || 0 })}
            </span>
            <span className="flex items-start gap-1 text-xs truncate line-clamp-2">
              {t("Reason: {{reason}}", { reason: reasonLabelMap[bookingPage.collectReason ?? "Optional"] })}
            </span>
          </div>
        </div>
      </Component>
      {(canDelete || canDuplicate) && (
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              data-testid={`booking-page-card-menu-${bookingPage.id}`}
              variant="ghost"
              size="sm-icon"
              className="invisible pointer-events-none group-hover:visible data-[state=open]:visible group-hover:pointer-events-auto data-[state=open]:pointer-events-auto absolute top-2 right-2"
            >
              <Ellipsis />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-1 w-auto" align="end">
            {canCreateNote &&
              (typeof onCreateNote === "object" ? (
                <Link
                  to={onCreateNote.to}
                  replace={onCreateNote.replace}
                  data-testid={`booking-page-card-create-note-${bookingPage.id}`}
                  className="transition-all font-light flex gap-2 items-center text-sm px-2 py-1 hover:bg-gray-100 rounded-sm"
                >
                  <FileText size={12} />
                  {t("Create Patient Booking Note")}
                </Link>
              ) : (
                <Button
                  onClick={onCreateNote}
                  data-testid={`booking-page-card-create-note-${bookingPage.id}`}
                  variant="ghost"
                  className="font-light"
                >
                  <FileText />
                  {t("Create Patient Booking Note")}
                </Button>
              ))}
            {canDuplicate &&
              (typeof onDuplicate === "object" ? (
                <Link
                  to={onDuplicate.to}
                  replace={onDuplicate.replace}
                  data-testid={`booking-page-card-duplicate-${bookingPage.id}`}
                  className="transition-all font-light flex gap-2 items-center text-sm px-2 py-1 hover:bg-gray-100 rounded-sm"
                >
                  <Copy size={12} />
                  {t("Duplicate")}
                </Link>
              ) : (
                <Button
                  onClick={onDuplicate}
                  data-testid={`booking-page-card-duplicate-${bookingPage.id}`}
                  variant="ghost"
                  className="font-light"
                >
                  <Copy />
                  {t("Duplicate")}
                </Button>
              ))}
            {bookingLink && (
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(bookingLink);
                  onCopyLink?.();
                  setPopoverOpen(false);
                }}
                variant="ghost"
                className="font-light py-1 h-auto"
              >
                <LinkIcon className="size-3" />
                {t("Copy Booking Link")}
              </Button>
            )}
            {canUpdate ? (
              typeof onClick === "object" ? (
                <Link
                  to={onClick.to}
                  replace={onClick.replace}
                  className="transition-all font-light flex gap-2 items-center text-sm px-2 py-1 hover:bg-gray-100 rounded-sm"
                >
                  <Pencil size={12} />
                  {t("Edit Page")}
                </Link>
              ) : (
                <Button onClick={onClick} variant="ghost" className="font-light">
                  {t("Edit Page")}
                </Button>
              )
            ) : null}
            {canDelete && (
              <>
                <div className="w-full bg-gray-200 h-[1px]" />
                <Button
                  data-testid={`booking-page-card-delete-${bookingPage.id}`}
                  variant="ghost"
                  className="text-destructive"
                  onClick={onDelete}
                >
                  <Trash2 />
                  {t("Delete")}
                </Button>
              </>
            )}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};
